import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellRing, X } from 'lucide-react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const API = '/api';
const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

const PushNotificationManager = () => {
  const { user } = useAuth();
  const [showPrompt, setShowPrompt] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;

    const dismissed = localStorage.getItem('push_prompt_dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 14 * 24 * 60 * 60 * 1000) return;
    }

    // Check existing subscription
    navigator.serviceWorker.ready.then((reg) => {
      reg.pushManager.getSubscription().then((sub) => {
        if (sub) {
          setSubscribed(true);
        } else {
          // Show prompt after delay
          setTimeout(() => setShowPrompt(true), 5000);
        }
      });
    });
  }, [user]);

  const handleSubscribe = useCallback(async () => {
    if (!VAPID_KEY) return;
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });

      const subJson = subscription.toJSON();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys,
      });

      setSubscribed(true);
      setShowPrompt(false);
      toast.success('Notifications enabled! We\'ll keep you updated on your orders.');
    } catch (err) {
      if (Notification.permission === 'denied') {
        toast.error('Notifications are blocked. Please enable them in your browser settings.');
      } else {
        toast.error('Could not enable notifications. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem('push_prompt_dismissed', Date.now().toString());
  }, []);

  if (!showPrompt || subscribed || !user) return null;

  return (
    <div
      data-testid="push-notification-prompt"
      className="fixed top-20 left-3 right-3 sm:left-auto sm:right-4 sm:w-[360px] z-[9998] animate-slide-in-right"
    >
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden">
        <div className="p-4 flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
            <BellRing className="w-5 h-5 text-[#D4AF37]" />
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-[#1A2B3C]">
              Never miss an order update
            </h4>
            <p className="text-xs text-slate-500 mt-0.5">
              Get notified when your order is shipped, ready for pickup, or delivered.
            </p>

            <div className="flex items-center gap-2 mt-3">
              <button
                data-testid="push-enable-button"
                onClick={handleSubscribe}
                disabled={loading}
                className="flex items-center gap-1.5 bg-[#1A2B3C] hover:bg-[#2A3B4C] text-white font-medium text-xs px-4 py-2 rounded-lg transition-colors"
              >
                <Bell className="w-3.5 h-3.5" />
                {loading ? 'Enabling...' : 'Enable'}
              </button>
              <button
                data-testid="push-dismiss-button"
                onClick={handleDismiss}
                className="text-slate-400 hover:text-slate-600 text-xs px-3 py-2 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>

          <button
            data-testid="push-close-button"
            onClick={handleDismiss}
            className="flex-shrink-0 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationManager;
